import React, { FC, useMemo, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { useQuery } from 'react-query';

import { fetchAllEventTypes } from 'core/API/events';
import { GetUsersList } from 'core/API/users';
import Loading from 'core/components/Loading/Loading';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { ScheduleResource, EventTypeResource } from 'types/scheduler';
import { Holiday } from 'types/holidays';
import { useCalendarView } from 'core/hooks/useCalendarView';
import {
	ScheduleEvent,
	ScheduleResource as ScheduleResourceComponent,
} from './ScheduleEvent.component';
import ScheduleEventsHeader from './ScheduleEventsHeader';
import './DesktopPage.style.scss';
import './FullCalendarSchedule.scss';

interface IFullCalendarSchedule {
	openEditShiftModal: (args: any) => void;
	openAddShiftModal: (args: any) => void;
	allEvents: MbscCalendarEvent[];
	holidays?: Holiday[];
	workplacesData: IWorkplaces;
	updateTimeRange: (firstday: Date, lastday: Date) => void;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	calendarType: string;
	openRequestsModal: (args: any) => void;
	openAddEventModal: (args: any) => void;
	openEditEventModal: (args: any) => void;
	refetch?: any;
	dragTimeStep?: number;
	resourcesOverride?: ScheduleResource[];
	renderHeaderOverride?: () => React.ReactElement | null;
	startOfDay?: string;
	startOfWeek?: string;
	workplaceWorkingHoursStart?: string;
	selectedDate?: Date;
	businessWeekStart?: string;
	onEditShift?: (event: MbscCalendarEvent) => void;
	onDeleteShift?: (event: MbscCalendarEvent) => void;
	onCopyShift?: (event: MbscCalendarEvent) => void;
	onPublish?: () => void;
	canManageShifts?: boolean;
	canDeleteShifts?: boolean;
	onShiftDragUpdate?: (event: any) => void;
	onSetLastFocusedDate?: (date: Date) => void;
}

const FullCalendarSchedule: FC<IFullCalendarSchedule> = ({
	openEditShiftModal: _openEditShiftModal,
	openAddShiftModal,
	allEvents,
	holidays: _holidays = [],
	workplacesData,
	updateTimeRange: _updateTimeRange,
	setCalendarType,
	calendarType,
	openRequestsModal: _openRequestsModal,
	openEditEventModal: _openEditEventModal,
	refetch: _refetch,
	dragTimeStep,
	resourcesOverride,
	renderHeaderOverride,
	startOfDay = '00:00',
	startOfWeek,
	workplaceWorkingHoursStart,
	selectedDate,
	businessWeekStart,
	onEditShift,
	onDeleteShift,
	onCopyShift,
	onPublish,
	canManageShifts = false,
	canDeleteShifts = false,
	onShiftDragUpdate,
	onSetLastFocusedDate,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const { calendarView } = useCalendarView();
	const calendarRef = useRef<FullCalendar>(null);

	const { user } = JSON.parse(localStorage.getItem('BYOD-app') || '{}');
	const { data: _usersData, isLoading: isLoadingUsersWorkplace } = useQuery(
		['users', user?.id],
		() => GetUsersList(user.id),
		{
			enabled: !!user?.id,
		}
	);

	const { data: eventTypes } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
		enabled: calendarType === 'event',
	});

	const eventResources = useMemo<EventTypeResource[]>(() => {
		const eventsListResource = eventTypes?.items?.map((eventType: any): EventTypeResource => {
			return {
				id: eventType.id,
				name: eventType.type,
			};
		});

		return eventsListResource ?? [];
	}, [eventTypes]);

	// Convert resources to FullCalendar format
	const fullCalendarResources = useMemo(() => {
		const resources: ScheduleResource[] =
			resourcesOverride ?? (eventResources && eventResources.length ? eventResources : []);

		return resources.map((resource) => ({
			id: String(resource.id),
			title: resource.name,
			extendedProps: {
				...resource,
			},
		}));
	}, [resourcesOverride, eventResources]);

	// Convert events to FullCalendar format
	const fullCalendarEvents = useMemo(() => {
		return allEvents.map((event: any) => {
			// Format time range for display
			const startTime = moment(event.start).format('h:mm A');
			const endTime = moment(event.end).format('h:mm A');
			const timeRange = `${startTime} - ${endTime}`;

			// For week view, normalize to day boundaries so events occupy full day slot
			let displayStart = event.start;
			let displayEnd = event.end;

			if (calendarView === 'week' || calendarView === 'workweek') {
				// Set to start of day (00:00:00)
				displayStart = moment(event.start).startOf('day').toDate();
				// Set to end of day (23:59:59)
				displayEnd = moment(event.start).endOf('day').toDate();
			}

			return {
				id: event.shift_id || event.id,
				resourceId: String(event.resource),
				title: event.title || '',
				start: displayStart,
				end: displayEnd,
				backgroundColor: event.background_color || event.color,
				borderColor: event.background_color || event.color,
				textColor: event.text_color || '#000',
				editable: canManageShifts && event.editable !== false,
				extendedProps: {
					...event,
					// Ensure startAt and endAt are preserved for ScheduleEvent component
					startAt: event.startAt || event.start,
					endAt: event.endAt || event.end,
					timeRange, // Add formatted time range for display
					actualStart: event.start, // Preserve actual times for operations
					actualEnd: event.end,
				},
			};
		});
	}, [allEvents, canManageShifts, calendarView]);

	// Calculate visible date range based on business week
	const visibleRange = useMemo(() => {
		if (!businessWeekStart) return undefined;

		const start = moment(businessWeekStart).toDate();
		const end = moment(businessWeekStart).add(7, 'days').toDate();

		return { start, end };
	}, [businessWeekStart]);

	// Scroll to selected date when it changes or when events are loaded
	useEffect(() => {
		if (calendarRef.current && selectedDate) {
			setTimeout(() => {
				// Get the calendar's root element from the container
				const calendarEl = document.querySelector('.fullcalendar-schedule-container .fc') as HTMLElement;
				
				if (!calendarEl) {
					console.log('Calendar element not found');
					return;
				}

				// For timeline views, we need to scroll the timeline body lanes container
				// This is where the actual events are rendered and needs horizontal scrolling
				let timelineElement = calendarEl.querySelector('.fc-timeline-body .fc-timeline-lane-frame') as HTMLElement;
				
				if (!timelineElement) {
					// Try finding the scrollable container for timeline lanes
					timelineElement = calendarEl.querySelector('.fc-timeline-body .fc-scroller') as HTMLElement;
				}
				
				if (!timelineElement) {
					// Try the main timeline scroller
					timelineElement = calendarEl.querySelector('.fc-scroller.fc-scroller-liquid-absolute') as HTMLElement;
				}
				
				// console.log('Scrolling to date:', selectedDate, { 
				// 	found: !!timelineElement,
				// 	businessWeekStart,
				// 	elementClass: timelineElement?.className,
				// 	scrollLeft: timelineElement?.scrollLeft,
				// 	scrollWidth: timelineElement?.scrollWidth,
				// 	clientWidth: timelineElement?.clientWidth,
				// 	parentElement: timelineElement?.parentElement?.className
				// });
				
				if (timelineElement && businessWeekStart) {
					// Calculate the horizontal scroll position based on date AND time
					const weekStart = moment(businessWeekStart);
					const targetDate = moment(selectedDate);
					const daysDiff = targetDate.diff(weekStart, 'days');
					
					// Find the actual scrollable parent if current element isn't scrollable
					let scrollableElement = timelineElement;
					if (timelineElement.scrollWidth <= timelineElement.clientWidth) {
						// Try to find parent that's scrollable
						let parent = timelineElement.parentElement;
						while (parent && parent.scrollWidth <= parent.clientWidth) {
							parent = parent.parentElement;
							if (parent?.classList.contains('fc-scroller')) {
								scrollableElement = parent as HTMLElement;
								break;
							}
						}
						// console.log('Found scrollable parent:', scrollableElement.className, {
						// 	scrollWidth: scrollableElement.scrollWidth,
						// 	clientWidth: scrollableElement.clientWidth
						// });
					}
					
					// Calculate scroll position based on proportion of the week
					// The business week is 7 days, so we calculate the position proportionally
					const totalDaysInWeek = 7;
					const maxScrollLeft = scrollableElement.scrollWidth - scrollableElement.clientWidth;
					
					// Calculate the base scroll position for the day
					const dayScrollPosition = (daysDiff / totalDaysInWeek) * scrollableElement.scrollWidth;
					
					// In day view, also calculate time offset within the day
					// In week view, scroll to start of day only
					let timeOffset = 0;
					let hours = 0;
					let minutes = 0;
					let timeInHours = 0;
					
					if (calendarView === 'day') {
						// Get hours and minutes from the selected date
						hours = targetDate.hours();
						minutes = targetDate.minutes();
						timeInHours = hours + minutes / 60;
						
						// Each day takes up (1/7) of the total scroll width
						const dayWidth = scrollableElement.scrollWidth / totalDaysInWeek;
						
						// Time offset within the day (0-24 hours maps to 0-dayWidth pixels)
						timeOffset = (timeInHours / 24) * dayWidth;
					}
					
					// Combine day position and time offset (timeOffset is 0 in week view)
					const scrollLeft = dayScrollPosition + timeOffset;
					
					// Clamp to valid scroll range
					const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));
					
					// console.log('Scrolling to date and time:', { 
					// 	calendarView,
					// 	daysDiff,
					// 	hours,
					// 	minutes,
					// 	timeInHours,
					// 	totalDaysInWeek,
					// 	scrollWidth: scrollableElement.scrollWidth,
					// 	clientWidth: scrollableElement.clientWidth,
					// 	dayScrollPosition,
					// 	timeOffset,
					// 	maxScrollLeft,
					// 	calculatedScroll: scrollLeft,
					// 	clampedScrollLeft,
					// 	currentScrollLeft: scrollableElement.scrollLeft 
					// });
					
					// Scroll to the exact date and time position
					scrollableElement.scrollLeft = clampedScrollLeft;
				}
			}, 1000);
		}
	}, [selectedDate, businessWeekStart, calendarView, fullCalendarEvents]);

	// Change view when calendarView changes
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			// Both day and week views show the entire business week
			// The difference is in slot duration (configured separately)
			const targetView = 'resourceTimelineWeek';
			if (calendarApi.view.type !== targetView) {
				calendarApi.changeView(targetView);
			}
		}
	}, [calendarView]);

	// Handle event click
	const handleEventClick = useCallback(
		(info: any) => {
			if (onEditShift && calendarType === 'schedule') {
				const originalEvent = info.event.extendedProps;
				// Track the focused date for scroll restoration
				if (onSetLastFocusedDate && (originalEvent.start || originalEvent.startAt)) {
					onSetLastFocusedDate(originalEvent.start || originalEvent.startAt);
				}
				onEditShift(originalEvent);
			}
		},
		[onEditShift, calendarType, onSetLastFocusedDate]
	);

	// Handle date click (cell click)
	const handleDateClick = useCallback(
		(info: any) => {
			if (!canManageShifts || calendarType !== 'schedule') return;

			// Track the focused date for scroll restoration
			if (onSetLastFocusedDate && info.date) {
				onSetLastFocusedDate(info.date);
			}

			// In day view, resources are job codes; in week view, resources are employees
			const modalData: any = {
				date: info.date,
			};

			if (calendarView === 'day') {
				// In day view, resource is job code
				modalData.jobCode = info.resource?.id;
			} else {
				// In week view, resource is employee
				modalData.resource = info.resource?.id;
			}

			openAddShiftModal(modalData);
		},
		[canManageShifts, calendarType, openAddShiftModal, onSetLastFocusedDate, calendarView]
	);

	// Handle event drop (drag and drop)
	const handleEventDrop = useCallback(
		(info: any) => {
			if (!canManageShifts) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;
			const delta = info.delta; // FullCalendar's time delta object

			let newStart: Date;
			let newEnd: Date;

			// For week view, use actual times and apply the day change
			if (calendarView === 'week' || calendarView === 'workweek') {
				// Calculate how many days the event moved
				const daysDiff = delta.days || 0;
				// Apply day difference to actual times (preserves time-of-day)
				newStart = moment(extendedProps.actualStart || extendedProps.startAt || extendedProps.start)
					.add(daysDiff, 'days')
					.toDate();
				newEnd = moment(extendedProps.actualEnd || extendedProps.endAt || extendedProps.end)
					.add(daysDiff, 'days')
					.toDate();
			} else {
				// For day view, use the new times directly from FullCalendar
				newStart = event.start;
				newEnd = event.end;
			}

			// Get the new resource ID (could be employee or jobCode depending on view)
			const newResourceId = event.getResources()[0]?.id;

			// Track the focused date for scroll restoration
			if (onSetLastFocusedDate && newStart) {
				onSetLastFocusedDate(newStart);
			}

			if (onShiftDragUpdate) {
				// Pass the complete event with updated times and resource
				onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: newResourceId,
						// Update employee or jobCode based on calendar view
						...(calendarView === 'week' || calendarView === 'workweek'
							? {
									employee:
										newResourceId === 'open-shifts'
											? null
											: newResourceId !== extendedProps.employee?.id
												? { id: newResourceId }
												: extendedProps.employee,
								}
							: {
									jobCode:
										newResourceId !== extendedProps.jobCode?.id
											? { id: newResourceId }
											: extendedProps.jobCode,
								}),
					},
				});
			}
		},
		[canManageShifts, onShiftDragUpdate, calendarView, onSetLastFocusedDate]
	);

	// Handle event resize
	const handleEventResize = useCallback(
		(info: any) => {
			if (!canManageShifts) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;

			// Resizing only works in day view (disabled in week view via eventDurationEditable)
			// Use the new times directly from FullCalendar
			const newStart = event.start;
			const newEnd = event.end;

			// Track the focused date for scroll restoration
			if (onSetLastFocusedDate && newStart) {
				onSetLastFocusedDate(newStart);
			}

			if (onShiftDragUpdate) {
				onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: event.getResources()[0]?.id,
					},
				});
			}
		},
		[canManageShifts, onShiftDragUpdate, onSetLastFocusedDate]
	);

	if (isLoadingUsersWorkplace) {
		return <Loading />;
	}

	return (
		<div className="fullcalendar-schedule-container">
			{renderHeaderOverride ? (
				renderHeaderOverride()
			) : (
				<ScheduleEventsHeader
					workplacesData={workplacesData}
					workplaceId={workplaceId}
					onSwitchCalendarType={setCalendarType}
					onAddShift={() =>
						openAddShiftModal({
							date: selectedDate || new Date(),
							resource: null,
						})
					}
					onPublish={onPublish}
					canManageShifts={canManageShifts}
				/>
			)}

			<FullCalendar
				ref={calendarRef}
				plugins={[resourceTimelinePlugin, interactionPlugin, momentTimezonePlugin]}
				schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
				initialView="resourceTimelineWeek"
				headerToolbar={false}
				timeZone="local"
				resources={fullCalendarResources}
				events={fullCalendarEvents}
				editable={canManageShifts && calendarType === 'schedule'}
				eventStartEditable={canManageShifts && calendarType === 'schedule'}
				eventDurationEditable={
					canManageShifts && calendarType === 'schedule' && calendarView === 'day'
				}
				droppable={canManageShifts && calendarType === 'schedule'}
				selectable={canManageShifts && calendarType === 'schedule'}
				selectMirror={true}
				dayMaxEvents={true}
				slotDuration={calendarView === 'day' ? '01:00:00' : '24:00:00'}
				slotLabelInterval={calendarView === 'day' ? '01:00:00' : '24:00:00'}
				slotLabelFormat={
					calendarView === 'day'
						? [
								// { month: 'long' }, // top level of text
								{ day: 'numeric', weekday: 'short', month: 'long' },
								{
									hour: 'numeric',
									minute: '2-digit',
									// day: 'numeric',
									omitZeroMinute: false,
									meridiem: 'short',
								},
							]
						: [
								{
									day: 'numeric',
									weekday: 'short',
									month: 'long',
								},
							]
				}
				// dayHeaderFormat={
				// 	calendarView === 'day'
				// 		? { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }
				// 		: { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }
				// }
				// slotLabelContent={
				// 	calendarView === 'day'
				// 		? undefined
				// 		: (arg) => {
				// 				// For week view, show date in slot labels
				// 				const date = moment(arg.date);
				// 				return (
				// 					<div style={{ textAlign: 'center' }}>
				// 						<div style={{ fontWeight: 600, fontSize: '14px' }}>{date.format('ddd')}</div>
				// 						<div style={{ fontSize: '12px' }}>{date.format('M/D')}</div>
				// 					</div>
				// 				);
				// 			}
				// }
				resourceAreaWidth="300px"
				resourceAreaHeaderContent={calendarView === 'day' ? 'Job Codes' : 'Employees'}
				height="auto"
				contentHeight="auto"
				aspectRatio={2}
				slotMinWidth={calendarView === 'week' || calendarView === 'workweek' ? 500 : 100}
				eventMinWidth={calendarView === 'week' || calendarView === 'workweek' ? 450 : 30}
				snapDuration={`00:${dragTimeStep || 15}:00`}
				eventOverlap={false}
				displayEventTime={calendarView === 'day'}
				displayEventEnd={calendarView === 'day'}
				slotMinTime={'00:00:00'}
				slotMaxTime="24:00:00"
				scrollTime={workplaceWorkingHoursStart || startOfDay || '08:00:00'}
				firstDay={
					startOfWeek
						? [
								'Sunday',
								'Monday',
								'Tuesday',
								'Wednesday',
								'Thursday',
								'Friday',
								'Saturday',
							].indexOf(startOfWeek)
						: 1
				}
				validRange={visibleRange}
				eventClick={handleEventClick}
				dateClick={handleDateClick}
				eventDrop={handleEventDrop}
				eventResize={handleEventResize}
				eventContent={(arg) => {
					const event = arg.event.extendedProps;
					return (
						<div className="fc-event-custom">
							<ScheduleEvent
								calendarType={calendarType}
								calendarView={calendarView}
								data={{ original: event } as any}
								onEditClick={canManageShifts ? onEditShift : undefined}
								onDeleteClick={canDeleteShifts ? onDeleteShift : undefined}
								onCopyClick={canManageShifts ? onCopyShift : undefined}
							/>
						</div>
					);
				}}
				resourceLabelContent={(arg) => {
					const resource = arg.resource.extendedProps;
					return (
						<ScheduleResourceComponent resource={resource as any} calendarType={calendarType} />
					);
				}}
			/>
		</div>
	);
};

export default FullCalendarSchedule;
